import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Clipboard, ClipboardCheck, ExternalLink, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import MasterDataSelect from "@/components/MasterDataSelect";
import DatePicker from "@/components/DatePicker";
import { useMasterList } from "@/hooks/useMasterData";
import { saleTypeSection } from "@/lib/assetScopes";
import { formatINR, formatNumber } from "@/lib/format";
import { ASSET_SCOPES, ARCHIVED_STATUSES } from "@/lib/assetScopes";

const emptyForm = {
  name: "",
  asset_category_id: "",
  asset_type_id: "",
  offer_price: "",
  offer_price_unit_id: "",
  area: "",
  area_unit_id: "",
  built_up_area: "",
  built_up_area_unit: "",
  asset_address: "",
  city_id: "",
  village_id: "",
  google_maps_link: "",
  dues: "",
  short_description: "",
  sale_type_id: "",
  bank_arc_id: "",
  possession_type_id: "",
  borrower_name: "",
  case_forum_id: "",
  asset_history: "",
  auction_date: "",
  emd_amount: "",
  emd_amount_unit_id: "",
  emd_due_date: "",
  bid_submission_date: "",
  emd_payment_instructions: "",
  documents_available: [],
};

function FieldLabel({ children, required }) {
  return (
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
      {children}
      {required && <span className="text-[#B8935A] ml-0.5">*</span>}
    </Label>
  );
}

export default function AssetForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [copied, setCopied] = useState(false);

  // Master data for inline computations
  const { data: priceUnits = [] } = useMasterList("price_unit");
  const { data: areaUnits = [] } = useMasterList("area_unit");
  const { data: saleTypes = [] } = useMasterList("sale_type");
  const { data: docTypes = [] } = useMasterList("document_type");

  const { data: existing } = useQuery({
    queryKey: ["asset", id],
    queryFn: async () => (await api.get(`/assets/${id}`)).data,
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        ...emptyForm,
        ...existing,
        built_up_area: existing.built_up_area ?? "",
        built_up_area_unit: existing.built_up_area_unit || "",
        google_maps_link: existing.google_maps_link || "",
        dues: existing.dues ?? "",
        short_description: existing.short_description || "",
        borrower_name: existing.borrower_name || "",
        asset_history: existing.asset_history || "",
        auction_date: existing.auction_date || "",
        emd_amount: existing.emd_amount ?? "",
        emd_amount_unit_id: existing.emd_amount_unit_id || "",
        emd_due_date: existing.emd_due_date || "",
        bid_submission_date: existing.bid_submission_date || "",
        emd_payment_instructions: existing.emd_payment_instructions || "",
        documents_available: existing.documents_available || [],
      });
    }
  }, [existing]);

  // New-asset scope prefill: pre-select the section's default Sale Type (user can change it).
  useEffect(() => {
    if (isEdit || saleTypes.length === 0) return;
    const scopeKey = searchParams.get("scope");
    const cfg = ASSET_SCOPES[scopeKey];
    if (!cfg) return;
    const match = saleTypes.find((s) => s.label === cfg.defaultSaleTypeLabel);
    if (match) setForm((f) => (f.sale_type_id ? f : { ...f, sale_type_id: match.id }));
  }, [isEdit, saleTypes, searchParams]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  // Computed
  const priceCF = useMemo(
    () => priceUnits.find((u) => u.id === form.offer_price_unit_id)?.conversion_factor || 0,
    [priceUnits, form.offer_price_unit_id]
  );
  const areaCF = useMemo(
    () => areaUnits.find((u) => u.id === form.area_unit_id)?.conversion_factor || 0,
    [areaUnits, form.area_unit_id]
  );
  const emdCF = useMemo(
    () => priceUnits.find((u) => u.id === form.emd_amount_unit_id)?.conversion_factor || 0,
    [priceUnits, form.emd_amount_unit_id]
  );
  const offerINR = Number(form.offer_price || 0) * priceCF;
  const areaSqft = Number(form.area || 0) * areaCF;
  const emdINR = Number(form.emd_amount || 0) * emdCF;

  const selectedSaleType = saleTypes.find((s) => s.id === form.sale_type_id);
  const isEAuction = (selectedSaleType?.label || "").toLowerCase().includes("e-auction");
  // Land & Other assets show a simplified form (Sale Type + Possession Type only, plus Basic Details).
  const isLand = saleTypeSection(selectedSaleType) === "resale";

  // Section-based Sale Type dropdown filtering (display-only; stored value always stays selectable).
  // Add: driven by ?scope=. Edit: driven by the asset's ORIGINAL stored Sale Type. Archived: no filter.
  // Section is read dynamically from each option's asset_section (falls back to label heuristic),
  // so new Sale Types added via Master Data appear in the right dropdown automatically.
  const originalSaleType = useMemo(
    () => saleTypes.find((s) => s.id === existing?.sale_type_id),
    [saleTypes, existing]
  );
  const saleTypeFilterSection = useMemo(() => {
    if (isEdit) {
      if (!existing) return null;
      if (ARCHIVED_STATUSES.includes(existing.status)) return null;
      return saleTypeSection(originalSaleType);
    }
    const scope = searchParams.get("scope");
    if (scope === "auction") return "auction";
    if (scope === "land") return "resale";
    return null;
  }, [isEdit, existing, originalSaleType, searchParams]);
  const saleTypeOptionFilter = useMemo(
    () => (saleTypeFilterSection ? (o) => saleTypeSection(o) === saleTypeFilterSection : undefined),
    [saleTypeFilterSection]
  );

  // Validation
  function validate() {
    const e = {};
    const req = [
      "name",
      "asset_category_id",
      "asset_type_id",
      "offer_price",
      "offer_price_unit_id",
      "area",
      "area_unit_id",
      "asset_address",
      "city_id",
      "village_id",
      "sale_type_id",
      "possession_type_id",
    ];
    if (!isLand) {
      // Auction-only mandatory fields (hidden + not required for Land & Other)
      req.push("bank_arc_id", "case_forum_id");
    }
    for (const f of req) {
      if (!form[f] && form[f] !== 0) e[f] = "Required";
    }
    if (form.offer_price && Number(form.offer_price) <= 0)
      e.offer_price = "Must be greater than 0";
    if (form.area && Number(form.area) <= 0) e.area = "Must be greater than 0";
    if (form.built_up_area && Number(form.built_up_area) <= 0)
      e.built_up_area = "Must be greater than 0";
    if (form.google_maps_link) {
      try {
        new URL(form.google_maps_link);
      } catch {
        e.google_maps_link = "Enter a valid URL";
      }
    }
    if (!isLand && form.emd_amount && Number(form.emd_amount) > 0) {
      if (!form.emd_due_date) e.emd_due_date = "Required when EMD Amount is entered";
      if (!form.emd_amount_unit_id) e.emd_amount_unit_id = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted errors");
      return;
    }
    const payload = { ...form };
    // numeric coercion
    payload.offer_price = Number(payload.offer_price);
    payload.area = Number(payload.area);
    if (payload.built_up_area === "" || payload.built_up_area === null) {
      payload.built_up_area = null;
      payload.built_up_area_unit = null;
    } else payload.built_up_area = Number(payload.built_up_area);
    if (payload.built_up_area_unit === "") payload.built_up_area_unit = null;
    if (payload.dues === "" || payload.dues === null) payload.dues = null;
    else payload.dues = Number(payload.dues);
    if (payload.emd_amount === "" || payload.emd_amount === null) {
      payload.emd_amount = null;
      payload.emd_amount_unit_id = null;
      payload.emd_due_date = null;
    } else payload.emd_amount = Number(payload.emd_amount);
    for (const k of ["google_maps_link", "short_description", "borrower_name", "asset_history", "auction_date", "bid_submission_date", "emd_payment_instructions"]) {
      if (payload[k] === "") payload[k] = null;
    }
    setSubmitting(true);
    try {
      const res = isEdit
        ? await api.put(`/assets/${id}`, payload)
        : await api.post("/assets", payload);
      toast.success(isEdit ? "Asset updated" : "Asset created");
      navigate(`/assets/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDoc = (docId) => {
    setForm((f) => {
      const set = new Set(f.documents_available || []);
      if (set.has(docId)) set.delete(docId);
      else set.add(docId);
      return { ...f, documents_available: Array.from(set) };
    });
  };

  return (
    <form onSubmit={onSubmit} className="pb-32" data-testid="asset-form">
      <div className="px-8 pt-8 max-w-5xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="back-button"
        >
          <ChevronLeft className="h-3 w-3" /> Back
        </button>
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="label-eyebrow">{isEdit ? "Edit Asset" : "Create New"}</div>
            <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">
              {isEdit ? form.name || "Asset" : "Add Asset"}
            </h1>
            {isEdit && existing?.asset_code && (
              <div className="font-mono-num text-xs text-slate-500 mt-1">{existing.asset_code}</div>
            )}
          </div>
        </div>
      </div>

      <div className="px-8 mt-6 max-w-5xl">
        <Accordion type="multiple" defaultValue={["s1", "s2", "s3", "docs"]} className="space-y-3">
          {/* Section 1 — Basic Details */}
          <AccordionItem value="s1" className="bg-white border border-slate-200 rounded-md" data-testid="asset-form-basic-accordion">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Section 1</div>
                <div className="font-display text-lg font-semibold text-slate-900">Basic Details</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <FieldLabel required>Asset Name</FieldLabel>
                  <Input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="e.g. 3BHK Flat at Powai - SBI"
                    className="mt-1"
                    data-testid="field-name"
                  />
                  {errors.name && <p className="text-xs text-red-700 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <FieldLabel required>Asset Category</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="asset_category"
                      value={form.asset_category_id}
                      onChange={(v) => {
                        set("asset_category_id", v);
                        set("asset_type_id", "");
                      }}
                      testId="field-asset-category"
                    />
                  </div>
                  {errors.asset_category_id && <p className="text-xs text-red-700 mt-1">{errors.asset_category_id}</p>}
                </div>
                <div>
                  <FieldLabel required>Asset Type</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="asset_type"
                      parentId={form.asset_category_id || null}
                      value={form.asset_type_id}
                      onChange={(v) => set("asset_type_id", v)}
                      disabled={!form.asset_category_id}
                      placeholder={form.asset_category_id ? "Select type…" : "Pick a category first"}
                      testId="field-asset-type"
                    />
                  </div>
                  {errors.asset_type_id && <p className="text-xs text-red-700 mt-1">{errors.asset_type_id}</p>}
                </div>
                <div>
                  <FieldLabel required>Offer Price</FieldLabel>
                  <div className="mt-1 flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.offer_price}
                      onChange={(e) => set("offer_price", e.target.value)}
                      placeholder="e.g. 1.5"
                      className="flex-1"
                      data-testid="field-offer-price"
                    />
                    <div className="w-28">
                      <MasterDataSelect
                        listType="price_unit"
                        value={form.offer_price_unit_id}
                        onChange={(v) => set("offer_price_unit_id", v)}
                        placeholder="Unit"
                        testId="field-offer-price-unit"
                      />
                    </div>
                  </div>
                  {offerINR > 0 && (
                    <p className="text-[11px] font-mono-num text-slate-500 mt-1">
                      = {formatINR(offerINR)}
                    </p>
                  )}
                  {(errors.offer_price || errors.offer_price_unit_id) && (
                    <p className="text-xs text-red-700 mt-1">
                      {errors.offer_price || errors.offer_price_unit_id}
                    </p>
                  )}
                </div>
                <div>
                  <FieldLabel required>Area</FieldLabel>
                  <div className="mt-1 flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.area}
                      onChange={(e) => set("area", e.target.value)}
                      placeholder="e.g. 1200"
                      className="flex-1"
                      data-testid="field-area"
                    />
                    <div className="w-32">
                      <MasterDataSelect
                        listType="area_unit"
                        value={form.area_unit_id}
                        onChange={(v) => set("area_unit_id", v)}
                        placeholder="Unit"
                        testId="field-area-unit"
                      />
                    </div>
                  </div>
                  {areaSqft > 0 && (
                    <p className="text-[11px] font-mono-num text-slate-500 mt-1">
                      = {formatNumber(Math.round(areaSqft))} sq.ft
                    </p>
                  )}
                  {(errors.area || errors.area_unit_id) && (
                    <p className="text-xs text-red-700 mt-1">
                      {errors.area || errors.area_unit_id}
                    </p>
                  )}
                </div>
                <div>
                  <FieldLabel>Built Up Area</FieldLabel>
                  <div className="mt-1 flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.built_up_area}
                      onChange={(e) => set("built_up_area", e.target.value)}
                      placeholder="e.g. 1000"
                      className="flex-1"
                      data-testid="field-built-up-area"
                    />
                    <div className="w-32">
                      <MasterDataSelect
                        listType="area_unit"
                        value={form.built_up_area_unit}
                        onChange={(v) => set("built_up_area_unit", v)}
                        placeholder="Unit"
                        testId="field-built-up-area-unit"
                      />
                    </div>
                  </div>
                  {errors.built_up_area && (
                    <p className="text-xs text-red-700 mt-1">{errors.built_up_area}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <FieldLabel required>Asset Address</FieldLabel>
                  <Textarea
                    value={form.asset_address}
                    onChange={(e) => set("asset_address", e.target.value)}
                    placeholder="Building, street, area, pincode…"
                    className="mt-1"
                    rows={3}
                    data-testid="field-asset-address"
                  />
                  {errors.asset_address && <p className="text-xs text-red-700 mt-1">{errors.asset_address}</p>}
                </div>
                <div>
                  <FieldLabel required>City</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="city"
                      value={form.city_id}
                      onChange={(v) => {
                        set("city_id", v);
                        set("village_id", "");
                      }}
                      testId="field-city"
                    />
                  </div>
                  {errors.city_id && <p className="text-xs text-red-700 mt-1">{errors.city_id}</p>}
                </div>
                <div>
                  <FieldLabel required>Village / Town</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="village_town"
                      parentId={form.city_id || null}
                      value={form.village_id}
                      onChange={(v) => set("village_id", v)}
                      disabled={!form.city_id}
                      placeholder={form.city_id ? "Select or type to add…" : "Pick a city first"}
                      allowAdd
                      testId="field-village"
                    />
                  </div>
                  {errors.village_id && <p className="text-xs text-red-700 mt-1">{errors.village_id}</p>}
                </div>
                <div>
                  <FieldLabel>Google Maps Link</FieldLabel>
                  <div className="mt-1 flex gap-2">
                    <Input
                      type="url"
                      value={form.google_maps_link}
                      onChange={(e) => set("google_maps_link", e.target.value)}
                      placeholder="https://maps.google.com/…"
                      className="flex-1"
                      data-testid="field-maps-link"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={!form.google_maps_link}
                      onClick={() => {
                        navigator.clipboard.writeText(form.google_maps_link);
                        setCopied(true);
                        toast.success("Copied to clipboard");
                        setTimeout(() => setCopied(false), 1500);
                      }}
                      data-testid="field-maps-copy"
                    >
                      {copied ? <ClipboardCheck className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
                    </Button>
                    {form.google_maps_link && (
                      <Button type="button" variant="outline" size="icon" asChild>
                        <a href={form.google_maps_link} target="_blank" rel="noreferrer" data-testid="field-maps-open">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                  {errors.google_maps_link && <p className="text-xs text-red-700 mt-1">{errors.google_maps_link}</p>}
                </div>
                <div>
                  <FieldLabel>Dues (₹)</FieldLabel>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.dues}
                    onChange={(e) => set("dues", e.target.value)}
                    placeholder="Outstanding dues in INR"
                    className="mt-1"
                    data-testid="field-dues"
                  />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>Short Description</FieldLabel>
                  <Textarea
                    value={form.short_description}
                    onChange={(e) => set("short_description", e.target.value)}
                    placeholder="One-paragraph summary…"
                    className="mt-1"
                    rows={2}
                    data-testid="field-short-description"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Section 2 — Sale & Legal Details */}
          <AccordionItem value="s2" className="bg-white border border-slate-200 rounded-md" data-testid="asset-form-sale-accordion">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Section 2</div>
                <div className="font-display text-lg font-semibold text-slate-900">{isLand ? "Pricing & Details" : "Sale & Legal Details"}</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FieldLabel required>Sale Type</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="sale_type"
                      value={form.sale_type_id}
                      onChange={(v) => set("sale_type_id", v)}
                      optionFilter={saleTypeOptionFilter}
                      testId="field-sale-type"
                    />
                  </div>
                  {errors.sale_type_id && <p className="text-xs text-red-700 mt-1">{errors.sale_type_id}</p>}
                </div>
                {!isLand && (
                <div>
                  <FieldLabel required>Bank / ARC Name</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="bank_arc"
                      value={form.bank_arc_id}
                      onChange={(v) => set("bank_arc_id", v)}
                      allowAdd
                      testId="field-bank-arc"
                    />
                  </div>
                  {errors.bank_arc_id && <p className="text-xs text-red-700 mt-1">{errors.bank_arc_id}</p>}
                </div>
                )}
                <div>
                  <FieldLabel required>Possession Type</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="possession_type"
                      value={form.possession_type_id}
                      onChange={(v) => set("possession_type_id", v)}
                      testId="field-possession-type"
                    />
                  </div>
                  {errors.possession_type_id && <p className="text-xs text-red-700 mt-1">{errors.possession_type_id}</p>}
                </div>
                <div>
                  <FieldLabel>{isLand ? "Owner Name" : "Borrower Name"}</FieldLabel>
                  <Input
                    value={form.borrower_name}
                    onChange={(e) => set("borrower_name", e.target.value)}
                    className="mt-1"
                    placeholder={isLand ? "Current owner / entity" : "Defaulting borrower / entity"}
                    data-testid="field-borrower-name"
                  />
                </div>
                {!isLand && (
                <div>
                  <FieldLabel required>Case Forum</FieldLabel>
                  <div className="mt-1">
                    <MasterDataSelect
                      listType="case_forum"
                      value={form.case_forum_id}
                      onChange={(v) => set("case_forum_id", v)}
                      testId="field-case-forum"
                    />
                  </div>
                  {errors.case_forum_id && <p className="text-xs text-red-700 mt-1">{errors.case_forum_id}</p>}
                </div>
                )}
                <div className="md:col-span-2">
                  <FieldLabel>{isLand ? "Asset Details" : "Asset History"}</FieldLabel>
                  <Textarea
                    value={form.asset_history}
                    onChange={(e) => set("asset_history", e.target.value)}
                    rows={4}
                    className="mt-1"
                    placeholder={isLand ? "Property details, ownership, notable features…" : "Chain of title, prior litigation, attempts at sale…"}
                    data-testid="field-asset-history"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Section 3 — Sale Process & Critical Dates (Auction only) */}
          {!isLand && (
          <AccordionItem value="s3" className="bg-white border border-slate-200 rounded-md" data-testid="asset-form-dates-accordion">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Section 3</div>
                <div className="font-display text-lg font-semibold text-slate-900">Sale Process & Critical Dates</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FieldLabel>Auction Date</FieldLabel>
                  <div className="mt-1">
                    <DatePicker
                      value={form.auction_date}
                      onChange={(d) => set("auction_date", d)}
                      testId="field-auction-date"
                    />
                  </div>
                  {errors.auction_date && <p className="text-xs text-red-700 mt-1">{errors.auction_date}</p>}
                </div>
                <div>
                  <FieldLabel>EMD Amount</FieldLabel>
                  <div className="mt-1 flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.emd_amount}
                      onChange={(e) => set("emd_amount", e.target.value)}
                      placeholder="e.g. 5"
                      className="flex-1"
                      data-testid="field-emd-amount"
                    />
                    <div className="w-28">
                      <MasterDataSelect
                        listType="price_unit"
                        value={form.emd_amount_unit_id}
                        onChange={(v) => set("emd_amount_unit_id", v)}
                        placeholder="Unit"
                        testId="field-emd-amount-unit"
                      />
                    </div>
                  </div>
                  {emdINR > 0 && (
                    <p className="text-[11px] font-mono-num text-slate-500 mt-1">
                      = {formatINR(emdINR)}
                    </p>
                  )}
                  {errors.emd_amount_unit_id && <p className="text-xs text-red-700 mt-1">{errors.emd_amount_unit_id}</p>}
                </div>
                <div>
                  <FieldLabel required={!!form.emd_amount}>EMD Due Date</FieldLabel>
                  <div className="mt-1">
                    <DatePicker
                      value={form.emd_due_date}
                      onChange={(d) => set("emd_due_date", d)}
                      testId="field-emd-due-date"
                    />
                  </div>
                  {errors.emd_due_date && <p className="text-xs text-red-700 mt-1">{errors.emd_due_date}</p>}
                </div>
                <div>
                  <FieldLabel>Bid Submission Date</FieldLabel>
                  <div className="mt-1">
                    <DatePicker
                      value={form.bid_submission_date}
                      onChange={(d) => set("bid_submission_date", d)}
                      testId="field-bid-submission-date"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>EMD Payment Instructions</FieldLabel>
                  <Textarea
                    value={form.emd_payment_instructions}
                    onChange={(e) => set("emd_payment_instructions", e.target.value)}
                    rows={3}
                    className="mt-1"
                    placeholder="Bank account details, portal links, etc."
                    data-testid="field-emd-instructions"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          )}

          {/* Documents Available checklist */}
          <AccordionItem value="docs" className="bg-white border border-slate-200 rounded-md" data-testid="asset-form-documents-accordion">
            <AccordionTrigger className="px-5 hover:no-underline">
              <div className="text-left">
                <div className="label-eyebrow">Checklist</div>
                <div className="font-display text-lg font-semibold text-slate-900">Documents Available</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <p className="text-xs text-slate-500 mb-3">
                Mark which documents exist for this asset (uploads happen on the detail page).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {docTypes.map((d) => {
                  const checked = (form.documents_available || []).includes(d.id);
                  return (
                    <label
                      key={d.id}
                      className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded text-sm hover:bg-slate-50 cursor-pointer"
                      data-testid={`doc-check-${d.id}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleDoc(d.id)} />
                      <span className="flex-1">{d.label}</span>
                      {d.review_status === "pending" && (
                        <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                          pending
                        </Badge>
                      )}
                    </label>
                  );
                })}
                <div className="col-span-full mt-2">
                  <DocTypeInlineAdd onAdded={(newId) => toggleDoc(newId)} />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-64 right-0 bg-white border-t border-slate-200 px-8 py-4 flex items-center justify-end gap-3 z-50 pr-44">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-button">
          Cancel
        </Button>
        <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="save-asset-button">
          <Save className="h-4 w-4 mr-1.5" />
          {submitting ? "Saving…" : isEdit ? "Update Asset" : "Save Asset"}
        </Button>
      </div>
    </form>
  );
}

function DocTypeInlineAdd({ onAdded }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/master-data/document_type", {
        list_type: "document_type",
        label: label.trim(),
        parent_id: null,
      });
      toast.success(`"${data.label}" added (pending review)`);
      onAdded?.(data.id);
      setLabel("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Add a new document type…"
        className="flex-1"
        data-testid="doc-type-add-input"
      />
      <Button type="button" variant="outline" disabled={busy || !label.trim()} onClick={add} data-testid="doc-type-add-button">
        Add
      </Button>
    </div>
  );
}
